/**
 * Haptics for the games, guarded so it can be called from anywhere without a
 * thought.
 *
 * Platform reality: this is the Vibration API, which Android Chrome, Samsung
 * Internet and Firefox support and which iOS Safari has never shipped. On an
 * iPhone every call here is a silent no-op — deliberately, rather than
 * reaching for the `<input switch>` trick, which is undocumented for this
 * purpose and not something to put in a health product.
 *
 * Two rules the patterns follow. They are SHORT (a tick is 12ms, a failure
 * 90ms) so nothing feels like a phone alarm. And they are a second channel
 * for something already on screen, never the only signal — a player with
 * vibration off must lose nothing.
 */

export const HAPTICS_ENABLED_KEY = "@HapticsEnabled";

/** Patterns, in the shape navigator.vibrate takes. */
export const HAPTIC = {
  /** One band woven, one tap landed. */
  tick: 12,
  /** A slot coming due — the one moment the game demands attention. */
  slotOpen: [22, 45, 22],
  /** The target changed under you. */
  swap: [16, 70, 16],
  /** A round or a day survived. */
  success: [14, 45, 26],
  /** A miss, a wrong item, a timeout. */
  fail: 90,
} as const;

export function hapticsSupported(): boolean {
  return (
    typeof navigator !== "undefined" && typeof navigator.vibrate === "function"
  );
}

/** True when the device asks for reduced motion. It is not a haptics
 *  setting, but it is the closest thing a browser offers to "do not do
 *  physical effects at me", so it flips the default off. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Defaults to on, except where reduced motion is asked for. An explicit
 *  choice always wins over both. */
export function hapticsEnabled(): boolean {
  try {
    const stored = localStorage.getItem(HAPTICS_ENABLED_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    // storage unavailable — fall through to the default
  }
  return !prefersReducedMotion();
}

export function setHapticsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(HAPTICS_ENABLED_KEY, String(enabled));
  } catch {
    // storage unavailable — the choice holds for this page only
  }
}

/**
 * Fires a pattern, if the device can and the player wants it.
 *
 * Silent about every failure: an unsupported browser, a refused call, a
 * backgrounded tab. Haptics are a garnish, and nothing in a game should ever
 * break because a phone declined to buzz.
 */
export function haptic(pattern: number | readonly number[]): void {
  if (!hapticsSupported() || !hapticsEnabled()) return;
  // A hidden tab has no thumb against it, and some browsers refuse anyway.
  if (typeof document !== "undefined" && document.hidden) return;

  try {
    navigator.vibrate(pattern as number | number[]);
  } catch {
    // some browsers throw rather than return false
  }
}

/** Stops anything currently running — used when a run ends, so a failure
 *  buzz cannot trail into the results screen. */
export function stopHaptics(): void {
  if (!hapticsSupported()) return;
  try {
    navigator.vibrate(0);
  } catch {
    // as above
  }
}
